import { useEffect, useRef, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { apiPost, apiStream } from '@/api/http';
import type { Access } from '@/api/admin';
import { createHexisChat, createHexisChatStream, type HexisReply } from '@marcianus/shared/hexis-client';
import { stripMd } from '@marcianus/shared/text';
import { ligarDialogo } from '@marcianus/shared/a11y';
import { HexisSymbol, HexisThinking } from '@marcianus/design/components';
import { useTranslation } from 'react-i18next';

interface Msg { role: 'user' | 'bot'; text: string }

// Cérebro do Hexis = contrato partilhado (packages/shared). O que muda é só a
// PERSPETIVA no context — aqui surface:hq-console, role:staff.
const hexisChat = createHexisChat((path, body) => apiPost(path, body));
// A MESMA rota, com progresso: os passos reais do cérebro chegam enquanto ele
// trabalha. Até aqui a consola esperava a resposta inteira — até 150 s com três
// pontinhos, sem forma de distinguir «a pensar» de «avariou».
const hexisChatStream = createHexisChatStream(async (path, body) => {
  const res = await apiStream(path, body);
  return { ok: res.ok, body: res.body };
});

// O ícone de envio é o MESMO desenho do `Icon name="send"` do lean. Vive aqui
// inline porque `packages/shared` é deliberadamente SEM React (o design system
// partilhado são tokens e CSS, não componentes) — e um <svg> de 2 linhas não
// justifica um segundo sistema de ícones na consola.
function SendIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

/**
 * HexisFab — o MESMO copiloto do app lean: mesmo cérebro (POST /hexis/chat com
 * RAG sobre /Contexto) e agora também o mesmo CORPO.
 *
 * Tinha o cérebro partilhado e um corpo próprio: `.hexis-panel/.hexis-msgs/
 * .hexis-msg/.hexis-suggest/.hexis-input` reimplementavam, com outros valores
 * (balões de 13.5px, fora da escala tipográfica), o que o MDS já define para a
 * conversa do lean. Duas conversas com o mesmo nome e desenhos diferentes.
 * Agora a conversa é a do sistema — `.chat-msgs/.chat-msg/.chat-avatar/
 * .chat-bubble/.chat-turn/.chat-typing/.chat-input` — as MESMAS classes que o
 * lean usa, do mesmo ficheiro (packages/shared/src/mds.css).
 *
 * O que legitimamente difere é o CHROME, porque as conchas diferem: o lean vive
 * numa moldura de telemóvel (FAB ancorado à tab bar, popup a preencher a
 * moldura), a consola vive num ecrã de secretária (FAB fixo ao canto, painel
 * acoplado). Essa diferença está isolada em `.hexis-fab`/`.hexis-panel` e é a
 * ÚNICA — a partir da borda do painel para dentro, é pixel a pixel o lean.
 *
 * SEPARAÇÃO DE RESPONSABILIDADE: o contexto enviado é `surface: hq-console,
 * role: staff` (operação da equipa); no www é o participante/organizador.
 * Mesmo cérebro, perspetivas diferentes.
 */
export function HexisFab() {
  const { t, i18n } = useTranslation('hq');
  const qc = useQueryClient();
  // A foto vem do Clerk, que é quem detém a identidade da equipa na consola.
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  // Atalhos da PERSPETIVA da equipa (o lean sugere eventos e conexões; aqui
  // sugere-se operação) — o formato é o mesmo, o conteúdo é que é o do papel.
  const [suggestions, setSuggestions] = useState<string[]>(() => [t('hexis.s1'), t('hexis.s2'), t('hexis.s3')]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [passos, setPassos] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const painelRef = useRef<HTMLDivElement>(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [msgs, open]);

  // Um `role="dialog"` que não fecha com Escape e não recebe o foco é um
  // diálogo só no nome. O comportamento vem de packages/shared — o mesmo que
  // o lean usa nas suas folhas.
  useEffect(() => {
    if (!open || !painelRef.current) return;
    return ligarDialogo(painelRef.current, () => setOpen(false));
  }, [open]);

  const send = async (text: string) => {
    const message = text.trim();
    if (!message || busy) return;
    setMsgs((m) => [...m, { role: 'user', text: message }]);
    setInput('');
    setPassos([]);
    setBusy(true);
    try {
      // O mesmo user_id que o /access/me confirmou — o Hexis personaliza por pessoa.
      const access = qc.getQueryData<Access>(['access']);
      const uid = (access?.user as { userId?: string; id?: string } | null | undefined)?.userId
        ?? (access?.user as { id?: string } | null | undefined)?.id ?? 'hq-staff';
      const req = { message, userId: uid, context: { surface: 'hq-console', role: 'staff' }, locale: i18n.language };
      let r: HexisReply;
      try {
        let final: HexisReply | null = null;
        for await (const chunk of hexisChatStream(req)) {
          if (chunk.type === 'step') setPassos((p) => [...p, chunk.content]);
          else if (chunk.type === 'done') final = chunk.payload;
        }
        if (!final) throw new Error('stream sem resposta');
        r = final;
      } catch {
        // Sem SSE (proxy a tamponar, rede a cair): a resposta chega pelo
        // caminho de sempre. Perde-se o progresso, nunca a resposta.
        r = await hexisChat(req);
      }
      // Diretiva "Hexis sem markdown": limpa a resposta como o app lean faz.
      setMsgs((m) => [...m, { role: 'bot', text: stripMd(r.reply || '') || t('hexis.noReply') }]);
      if (r.suggestions?.length) setSuggestions(r.suggestions.slice(0, 3));
    } catch {
      setMsgs((m) => [...m, { role: 'bot', text: t('hexis.offline') }]);
    } finally {
      setBusy(false);
      setPassos([]);
    }
  };

  return (
    <>
      {open && (
        <div className="hexis-panel" role="dialog" aria-modal="true" aria-label="Hexis AI" ref={painelRef}>
          <header className="chat-header">
            <span className="brand-badge sm" aria-hidden><HexisSymbol size={20} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 'var(--fs-md)', fontWeight: 700 }}>Hexis AI</div>
              <div style={{ fontSize: 'var(--fs-label)', color: 'var(--fg-40)' }}>
                {t('hexis.subtitle')}
              </div>
            </div>
            <button className="icon-btn" onClick={() => setOpen(false)} aria-label={t('hexis.close')}>✕</button>
          </header>

          <div className="chat-msgs" ref={scrollRef} role="log" aria-live="polite">
            {/* Vazio INTENCIONAL, igual ao do lean: o centro do painel diz o que o
                Hexis sabe fazer, em vez de uma linha cinzenta encostada ao topo. */}
            {msgs.length === 0 && (
              <div style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', textAlign: 'center', gap: 'var(--sp-3)', padding: '0 var(--sp-6)',
              }}>
                <span className="brand-badge" aria-hidden><HexisSymbol size={26} /></span>
                <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 800, letterSpacing: '-0.01em' }}>
                  {t('hexis.askTitle')}
                </div>
                <p style={{ color: 'var(--fg-60)', fontSize: 'var(--fs-body)', maxWidth: '32ch', margin: 0 }}>
                  {t('hexis.askBody')}
                </p>
              </div>
            )}

            {msgs.map((m, i) => (
              <div key={i} className={`chat-msg${m.role === 'user' ? ' mine' : ''}`}>
                {/* Nas mensagens da PESSOA, a foto dela. Estava um ponto («·»)
                    — um marcador sem informação nenhuma. Degrada para as
                    iniciais e só depois para o ponto. */}
                <span className="chat-avatar" aria-hidden>
                  {m.role === 'bot' ? <HexisSymbol size={15} />
                    : user?.imageUrl
                      ? <img src={user.imageUrl} alt="" loading="lazy"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                      : <span style={{ fontSize: 'var(--fs-micro)', fontWeight: 700, color: 'var(--fg-60)' }}>
                          {(user?.fullName || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('')}
                        </span>}
                </span>
                <div className="chat-turn">
                  <div className="chat-bubble">{m.text}</div>
                </div>
              </div>
            ))}

            {busy && (
              <div className="chat-msg">
                <span className="chat-avatar" aria-hidden><HexisSymbol size={15} /></span>
                <HexisThinking steps={passos} />
              </div>
            )}
          </div>

          {suggestions.length > 0 && (
            <div className="h-scroll" style={{ padding: '0 var(--gutter) 10px' }}>
              {suggestions.map((s) => (
                <button key={s} type="button" className="chip purple" disabled={busy}
                  onClick={() => send(s)}>{s}</button>
              ))}
            </div>
          )}

          <form className="chat-input" onSubmit={(e) => { e.preventDefault(); send(input); }}>
            <input className="input" placeholder={t('hexis.placeholder')} value={input}
              onChange={(e) => setInput(e.target.value)} disabled={busy} aria-label={t('hexis.messageLabel')} />
            <button className="btn btn-sm btn-purple" type="submit" aria-label={t('hexis.send')}
              disabled={busy || !input.trim()}><SendIcon size={16} /></button>
          </form>
        </div>
      )}
      <button className="hexis-fab" aria-label={t('hexis.open')} onClick={() => setOpen((v) => !v)}>
        <HexisSymbol size={28} />
      </button>
    </>
  );
}
